import { useQuery } from "@tanstack/react-query";
import { LibraryAPI } from "@/lib/sprint9-api";
import {
  BookOpen, Layers, Send, AlertTriangle, BadgeCheck, Banknote, Loader2, TrendingUp, User as UserIcon,
} from "lucide-react";

const CAT_PALETTE = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#84cc16"];

function compactINR(n) {
  if (!n) return "0";
  if (n >= 1e5) return `${(n / 1e5).toFixed(2)} L`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return n.toFixed(0);
}

export default function LibraryDashboard() {
  const q = useQuery({ queryKey: ["lib-dashboard"], queryFn: () => LibraryAPI.dashboard() });

  if (q.isLoading) {
    return <div className="py-16 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…</div>;
  }
  const d = q.data;
  if (!d) return null;

  return (
    <div className="space-y-5" data-testid="library-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={<BookOpen className="h-4 w-4" />} label="Titles" value={d.totals.titles} tone="sky" testId="kpi-titles" />
        <Kpi icon={<Layers className="h-4 w-4" />} label="Total copies" value={d.totals.total_copies} tone="emerald" sub={`${d.totals.available_copies} available`} testId="kpi-copies" />
        <Kpi icon={<Send className="h-4 w-4" />} label="Active loans" value={d.totals.active_loans} tone="violet" testId="kpi-active" />
        <Kpi icon={<AlertTriangle className="h-4 w-4" />} label="Overdue" value={d.totals.overdue_loans} tone="rose" testId="kpi-overdue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="bg-card rounded-2xl shadow-sm p-5">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
            <BadgeCheck className="h-3.5 w-3.5 text-emerald-600" /> Fines collected
          </div>
          <div className="font-display font-extrabold text-2xl tabular-nums" data-testid="fines-collected">
            ₹{compactINR(d.fines.collected)}
          </div>
        </div>
        <div className="bg-card rounded-2xl shadow-sm p-5">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
            <Banknote className="h-3.5 w-3.5 text-amber-600" /> Fines outstanding
          </div>
          <div className="font-display font-extrabold text-2xl tabular-nums text-amber-600" data-testid="fines-outstanding">
            ₹{compactINR(d.fines.outstanding)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <UserIcon className="h-3.5 w-3.5 text-primary" />
            <h3 className="font-semibold text-sm">Top borrowers</h3>
          </div>
          {d.top_borrowers.length === 0 ? (
            <div className="p-4 text-xs text-muted-foreground">No loans yet.</div>
          ) : (
            <ul className="divide-y divide-border" data-testid="top-borrowers">
              {d.top_borrowers.map((b, idx) => (
                <li key={b.borrower_id} className="px-4 py-2 flex items-center gap-3" data-testid={`borrower-${b.borrower_id}`}>
                  <div className="h-7 w-7 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center">{idx + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{b.name}</div>
                    <div className="text-[10px] text-muted-foreground capitalize">{b.borrower_type} · {b.loans} loans{b.active ? ` · ${b.active} active` : ""}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <TrendingUp className="h-3.5 w-3.5 text-primary" />
            <h3 className="font-semibold text-sm">Popular titles</h3>
          </div>
          {d.popular_titles.length === 0 ? (
            <div className="p-4 text-xs text-muted-foreground">No loans yet.</div>
          ) : (
            <ul className="divide-y divide-border" data-testid="popular-titles">
              {d.popular_titles.map((t, idx) => (
                <li key={t.book_id} className="px-4 py-2 flex items-center gap-3" data-testid={`title-${t.book_id}`}>
                  <div className="h-7 w-7 rounded-full bg-emerald-500/10 text-emerald-600 text-[10px] font-bold flex items-center justify-center">{idx + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{t.title}</div>
                    <div className="text-[10px] text-muted-foreground">{t.loans} loans</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <BookOpen className="h-3.5 w-3.5 text-primary" />
            <h3 className="font-semibold text-sm">By category</h3>
          </div>
          {d.by_category.length === 0 ? (
            <div className="p-4 text-xs text-muted-foreground">No books yet.</div>
          ) : (
            <div className="px-4 py-3 space-y-2.5" data-testid="categories-list">
              {d.by_category.map((c, idx) => {
                const max = d.by_category[0]?.titles || 1;
                const pct = (c.titles / max) * 100;
                return (
                  <div key={c.category} data-testid={`cat-${c.category}`}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium">{c.category}</span>
                      <span className="tabular-nums text-muted-foreground">{c.titles} titles · {c.copies} copies</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: CAT_PALETTE[idx % CAT_PALETTE.length] }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, tone, sub, testId }) {
  const tones = {
    sky: "from-sky-50 to-white border-sky-200 text-sky-800",
    emerald: "from-emerald-50 to-white border-emerald-200 text-emerald-800",
    violet: "from-violet-50 to-white border-violet-200 text-violet-800",
    rose: "from-rose-50 to-white border-rose-200 text-rose-800",
  }[tone] || "from-slate-50 to-white border-border";
  return (
    <div data-testid={testId} className={`bg-gradient-to-br ${tones} border rounded-md p-4 hover:-translate-y-0.5 hover:shadow-md transition-all duration-300`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold mb-1.5">
        {icon}{label}
      </div>
      <div className="font-display font-extrabold text-2xl tabular-nums">{value || 0}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}
